import './App.css'
import './Aws.js'
import { dashboardResponse, registerSitesResponse } from './Aws.js'

import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { AmplifyAuthContainer, AmplifyAuthenticator, AmplifySignUp, AmplifySignIn } from '@aws-amplify/ui-react'
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components'
import { Auth } from 'aws-amplify'

import { Box, TextField, Stack, CircularProgress, Grid, Typography, Button } from '@mui/material'

function App() {
  const [authState, setAuthState] = React.useState()
  const [user, setUser] = React.useState()
  const [sites, setSites] = React.useState([])
  const [error, setError] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [formShopID, setFormShopID] = React.useState("")
  const [formPostCode, setFormPostCode] = React.useState("")
  const [formEmail, setFormEmail] = React.useState("")
  const [registering, setRegistering] = React.useState(false)
  const [registerError, setRegisterError] = React.useState("")
  const [searchParams] = useSearchParams()
  
  React.useEffect(() => {
    return onAuthUIStateChange((nextAuthState, authData) => {
      setAuthState(nextAuthState)
      setUser(authData)

      getDashboardResponse()
    })
  }, [])

  React.useEffect(() => {
    if (sites.length > 0) {
      // reload refresh token to get correct cookie claims
      Auth.currentAuthenticatedUser({bypassCache: true}).then(() => {
        if (process.env.REACT_APP_ENV === 'prod') {
          const callback = searchParams.get('callback')
          window.location.href = callback ? callback : "https://shopmate.co.uk"
        }
      })
    }
  }, [sites, searchParams])

  const getDashboardResponse = () => {
    setLoading(true)

    dashboardResponse()
      .then(async res => {
        console.log(res)
        if (res && !('errorType' in res)) {
          setSites(res.sites)
          setLoading(false)
        } else {
          // this should be no org
          if (!res) {
            setError(true)
          } else {
            if (res.errorType === 'Error') {
              setSites([])
            }
          }
          setLoading(false)
        }
      })
      .catch(async err => {
        if (authState === AuthState.SignedIn && user) {
          setError(true)
          setLoading(false)
        }
        console.log(err)
      })
  }

  const handleChange = e => {
    const { name, value } = e.target
    
    switch (name) {
      case 'shopid':
        setFormShopID(value)
        break;
      case 'postcode':
        setFormPostCode(value)
        break;
      case 'email':
        setFormEmail(value)
        break;
      default:
        break;
    }
  }

  const handleSubmit = () => {
    setRegistering(true)
    setRegisterError("")

    const bodyData = {
      payload: {
        shopID: parseInt(formShopID.trim()),
        postCode: formPostCode.trim(),
        email: formEmail.trim()
      }
    }

    registerSitesResponse(bodyData)
      .then(async (res) => {
        console.log(res)
        if (res && !('errorType' in res)) {
          if (res.success === true) {
            getDashboardResponse()
          } else {
            switch (res.code) {
              case 103:
                setRegisterError("This shop has already been registered, please contact RDP Support Team for assistance")
                break;
              case 102:
                setRegisterError("Shop could not be found for these credentials; please try again or contact RDP Support Team for assistance")
                break;
              default:
                setRegisterError("There was an error registering your shop, please contact RDP Support Team for assistance")
                break;
            }
          }
        } else {
          setRegisterError("There was an error registering your shop, please try again...")
        }

        setRegistering(false)
      })
      .catch(async err => {
        console.log(err)
        setRegisterError("There was an error registering your shop, please try again...")
      })
  }

  const renderRegistrationForm = () => {
    return (
      <Grid container spacing={1} direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '80vh', minWidth: '100vw' }}>
        <Grid item>
         <Typography variant="h4">Register your shop</Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">Gain access to your customised insights and reporting suite</Typography>
        </Grid>
        <Grid item>
          <Box
            component="form"
            sx={{
              '& .MuiTextField-root': { m: 1, width: '25ch' },
            }}
            noValidate
            autoComplete="off"
          >
            <Stack spacing={2}>
              <TextField
                id="shopid"
                name="shopid"
                label="Shop ID"
                type="tel"
                defaultValue=""
                variant="standard"
                onChange={handleChange}
              />
              <TextField
                id="postcode"
                name="postcode"
                label="Post Code"
                defaultValue=""
                variant="standard"
                onChange={handleChange}
              />
              <TextField
                id="email"
                name="email"
                label="Registered Email"
                defaultValue=""
                variant="standard"
                helperText="Email registered with RDP *"
                onChange={handleChange}
              />
              { registering ? 
                <Button variant="contained" disabled><CircularProgress size={24} color="inherit"/></Button>
                :
                formShopID && formPostCode && formEmail ?
                  <Button variant="contained" onClick={handleSubmit}>SUBMIT</Button>
                  :
                  <Button variant="contained" disabled>SUBMIT</Button>
              }
            </Stack>
          </Box>
        </Grid>
        { registerError ? <Grid item><Typography variant="caption" color="red">{registerError}</Typography></Grid> : null }
        <Grid item>
          <Box sx={{ height: '50px' }}></Box>
        </Grid>
        <Grid item>
          <Typography variant="caption">* For assistance or support please call RDP Support Team on 01780 480562</Typography>
        </Grid>
      </Grid>
    )
  }

  const renderError = () => {
    return (
      <Grid container spacing={2} alignItems="center" justifyContent="center" style={{ minHeight: '100vh', minWidth: '100vw' }}>
        <Grid item>
          <h1>Error logging in, please try again...</h1>
        </Grid>
      </Grid>
    )
  }

  const renderLoading = () => {
    return (
      <Grid container spacing={2} alignItems="center" justifyContent="center" style={{ minHeight: '100vh', minWidth: '100vw' }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  const renderLogin = () => {
    return (
      <AmplifyAuthContainer>
        <AmplifyAuthenticator usernameAlias="email">
          <AmplifySignUp
            slot="sign-up"
            usernameAlias="email"
            formFields={[
              {
                type: 'email',
                label: 'Email Address *',
                placeholder: 'Enter your email address'
              },
              {
                type: 'password',
                label: 'Password *',
                placeholder: 'Enter your password'
              }
            ]}
          />
          <AmplifySignIn slot="sign-in" usernameAlias="email" />
        </AmplifyAuthenticator>
      </AmplifyAuthContainer>
    )
  }

  if (error) {
    return renderError()
  } else if (authState === AuthState.SignedIn && user) {
    if (loading) {
      return renderLoading()
    } else {
      if (sites.length > 0) {
        return renderLoading()
      } else {
        return renderRegistrationForm()
      }
    }
  } else {
    return renderLogin()
  }
}

export default App;
